import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Mail, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { BACKEND_URL } from '../utils/api';
import { ArshHeader } from '../components/ArshHeader';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    setError('');
    try {
      await axios.post(`${BACKEND_URL}/api/auth/forgot-password`, { email });
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: '#000000' }}>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm">
        <div className="text-center mb-8">
          <ArshHeader />
        </div>

        <div className="rounded-2xl p-6 border border-[#D4AF37]/15" style={{ background: 'linear-gradient(180deg, #0c0c14 0%, #05050A 100%)' }}>
          {sent ? (
            <div className="text-center py-4" data-testid="forgot-password-success">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#D4AF37]/20 border border-[#D4AF37]/30 flex items-center justify-center" style={{ boxShadow: '0 0 20px rgba(212,175,55,0.2)' }}>
                <CheckCircle className="text-[#D4AF37]" size={32} />
              </div>
              <h2 className="font-['Cormorant_Garamond'] text-xl font-light text-white mb-2">Check your email</h2>
              <p className="font-['Outfit'] text-sm text-white/40 mb-6">
                We've sent a password reset link to <strong className="text-[#D4AF37]">{email}</strong>. Check your inbox and follow the instructions.
              </p>
              <a href="/" className="inline-flex items-center gap-2 text-[#D4AF37]/70 font-['Outfit'] text-sm font-medium hover:text-[#D4AF37] transition-colors" data-testid="back-to-login-link">
                <ArrowLeft size={16} /> Back to Sign In
              </a>
            </div>
          ) : (
            <>
              <div className="text-center mb-6">
                <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/30 flex items-center justify-center" style={{ boxShadow: '0 0 15px rgba(212,175,55,0.15)' }}>
                  <Mail className="text-[#D4AF37]" size={24} />
                </div>
                <h2 className="font-['Cormorant_Garamond'] text-xl font-light text-white mb-1">Forgot Password?</h2>
                <p className="font-['Outfit'] text-sm text-white/30">
                  Enter your email and we'll send you a reset link.
                </p>
              </div>

              {error && (
                <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-2 rounded-lg font-['Outfit'] text-sm mb-4" data-testid="forgot-password-error">{error}</div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <input type="email" placeholder="Enter your email" value={email} onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-[#D4AF37]/15 bg-white/5 focus:outline-none focus:ring-1 focus:ring-[#D4AF37]/40 font-['Outfit'] text-sm text-white/90 placeholder-white/20"
                  required data-testid="forgot-password-email-input" />
                <button type="submit" disabled={loading}
                  className="w-full bg-[#D4AF37] text-black py-3 rounded-full font-['Outfit'] font-semibold hover:shadow-[0_0_15px_rgba(212,175,55,0.3)] transition-all disabled:opacity-50"
                  data-testid="forgot-password-submit-button"
                >
                  {loading ? 'Sending...' : 'Send Reset Link'}
                </button>
              </form>

              <div className="text-center mt-4">
                <a href="/" className="inline-flex items-center gap-2 text-[#D4AF37]/60 font-['Outfit'] text-sm font-medium hover:text-[#D4AF37] transition-colors" data-testid="back-to-login-from-forgot">
                  <ArrowLeft size={16} /> Back to Sign In
                </a>
              </div>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
}
